import { useEffect, useMemo, useState } from "react";
import { useFormStore } from "@/widgets/classic-form/form";
import type { PersonSuggestionItem } from "@/entities/tree-person";
import { Carousel } from "@/shared/ui/ancestry";
import { QuickFillItem } from "./QuickFillItem";
import styles from "./QuickFillCarousel.module.scss";

interface QuickFillCarouselProps {
    suggestions: PersonSuggestionItem[];
    treeId: string;
    onSelect: (suggestion: PersonSuggestionItem, treeId: string) => void;
}

export const QuickFillCarousel = ({ suggestions, treeId, onSelect }: QuickFillCarouselProps) => {
    const selectedPersonId = useFormStore((s) => s.values.treePerson.personId);
    const [ready, setReady] = useState(false);

    // The Carousel's vanilla JS runtime (window.ui.carousel) measures slide widths
    // synchronously during initialization to determine which slides are focusable
    // (via tabIndex/aria-hidden). If the container hasn't been laid out yet at that
    // moment, all slides are marked unfocusable. Deferring the mount by one tick
    // ensures the parent container has a real width before the Carousel initializes.
    useEffect(() => {
        setReady(true);
    }, []);

    // Derive the initial slide index from the selected person so the carousel
    // scrolls to the relevant tile on remount. We depend only on `suggestions` —
    // not `selectedPersonId` — so clicking an already-visible tile doesn't cause
    // an unwanted scroll jump. The key-based remount (below) ensures this memo
    // re-evaluates whenever the suggestion list changes.

    const initialSlide = useMemo(() => {
        if (!selectedPersonId) return 0;
        const idx = suggestions.findIndex((s) => s.PersonId === selectedPersonId);
        return idx >= 0 ? idx : 0;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [suggestions]);

    if (!ready) return null;

    // Using a key derived from suggestions forces a full remount when the list changes,
    // ensuring the Carousel re-initializes with fresh DOM measurements rather than
    // relying on imperative repopulation which doesn't restore slide focusability.
    const carouselKey = suggestions.map((s) => s.PersonId).join(",");

    return (
        <Carousel
            key={carouselKey}
            className={styles.carousel}
            padded
            fullWidth={false}
            currentSlide={initialSlide}
            noFocusChange
        >
            {suggestions.map((suggestion) => (
                <Carousel.Slide key={suggestion.PersonId}>
                    <QuickFillItem suggestion={suggestion} treeId={treeId} onSelect={onSelect} />
                </Carousel.Slide>
            ))}
        </Carousel>
    );
};
